// React core
import React, { createContext, useContext, useReducer, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

// Import schemas and utilities
import { appStateSchema } from './_shared/appStateSchema';
import { batchStateSchema } from './_shared/batchStateSchema'; // Import batch schema
import { redistributionStateSchema } from './_shared/redistributionStateSchema';
import { pageSchemas } from './pages'; // This should export an object like { pageName1: schema1, ... }
import { localSettings } from '@utils/localSettings';
import ProviderError from '@types/ProviderError'; // Import the custom error
import { StateActionValidator } from './StateActionValidator'; // Import the validator
import { useLogger } from '@providers/loggerProvider';

// Create context
const AppStateContext = createContext();

// Helper functions
const _getNestedValue = (obj, path) => {
    if (obj == null) return undefined;
    if (!path) return obj;
    return path.split('.').reduce((current, key) => current?.[key], obj);
};

const _setNestedValueImmutable = (obj, path, value) => {
    if (!path) {
         if (obj == null) return value;
         console.warn("_setNestedValueImmutable: Attempted to set value without a path on an existing object. Operation ignored.");
         return obj;
    }

    const keys = path.split('.');
    const lastKey = keys.pop();
    const newObj = { ...(obj || {}) }; 

    let currentLevel = newObj;
    for (const key of keys) {
        const currentVal = currentLevel[key];
        currentLevel[key] = (currentVal && typeof currentVal === 'object' && !Array.isArray(currentVal))
            ? { ...currentVal }
            : {}; 
        currentLevel = currentLevel[key];
    }

    currentLevel[lastKey] = value;
    return newObj;
};

const _shouldPersistProperty = (schemaSlice, path) => {
    if (!schemaSlice || !path) return false;
    let currentSchema = schemaSlice;
    const pathParts = path.split('.');

    for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        let properties = null;
        if (currentSchema.type === 'object' && currentSchema.properties) {
            properties = currentSchema.properties;
        } else if (typeof currentSchema === 'object' && !currentSchema.type && !currentSchema.properties && Object.keys(currentSchema).length > 0) {
             properties = currentSchema;
        } else if (currentSchema.type === 'array') {
             return currentSchema.persist === true;
        } else {
            return false; 
        }
        const partSchema = properties?.[part];
        if (!partSchema) return false; 
        if (partSchema.persist === true) return true;
        if (partSchema.persist === false) return false;
        currentSchema = partSchema;
         if (i === pathParts.length - 1) {
             return partSchema.persist === true; 
         }
    }
    return false; 
};

const _getSchemaProperty = (schemaSlice, path) => {
     if (!schemaSlice || !path) return null;
     let currentSchema = schemaSlice;
     const pathParts = path.split('.');
     for (const part of pathParts) {
         let propertiesContainer = null;
         if (currentSchema.type === 'object' && currentSchema.properties) {
             propertiesContainer = currentSchema.properties;
         } else if (currentSchema.type === 'array' && currentSchema.itemSchema) {
              console.warn(`_getSchemaProperty: Attempting to traverse into array path '${path}'.`);
              if (pathParts[pathParts.length - 1] === part) {
                  return currentSchema;
              } else {
                  return null;
              }
         } else if (typeof currentSchema === 'object' && !currentSchema.type && !currentSchema.properties) {
             propertiesContainer = currentSchema;
         } else {
              return null;
         }
         if (!propertiesContainer || typeof propertiesContainer !== 'object' || !propertiesContainer.hasOwnProperty(part)) {
             return null;
         }
         currentSchema = propertiesContainer[part];
         if (!currentSchema) return null;
     }
     return currentSchema;
};

const _getLocalStorageKey = (stateContextPath, relativePath) => {
    if (!relativePath) { 
        return `state.${stateContextPath}`;
    }
    return `state.${stateContextPath}.${relativePath}`;
};

// _initializeState refactored to handle distinct global and page-specific 'batches'
const _initializeState = (logger) => {
    const state = { pages: {} }; // Ensure pages object is always present

    // Helper to process properties within a schema slice (remains largely the same)
    const processSchemaSlice = (schemaSlice, stateContextPathForStorage, loggerInstance) => {
        const propertiesToProcess = schemaSlice.properties || schemaSlice;
        let currentContextState = {};

        if (!propertiesToProcess || typeof propertiesToProcess !== 'object') {
            loggerInstance.warn(`Invalid properties structure for context '${stateContextPathForStorage}'.`, { propertiesToProcess });
            return {};
        }

        Object.entries(propertiesToProcess).forEach(([key, propSchema]) => {
            if (!propSchema || typeof propSchema !== 'object') {
                loggerInstance.warn(`Invalid schema for property '${key}' at context '${stateContextPathForStorage}'. Skipping.`);
                return;
            }
            const currentRelativePath = key;
            const isPersisted = _shouldPersistProperty(schemaSlice, currentRelativePath);
            let persistedValue = undefined;
            if (isPersisted) {
                persistedValue = localSettings.get(_getLocalStorageKey(stateContextPathForStorage, currentRelativePath));
            }

            let finalValueToSet;
            if (propSchema.type === 'object' && propSchema.properties) {
                const schemaDefaultObject = ('default' in propSchema && typeof propSchema.default === 'object' && propSchema.default !== null)
                                          ? propSchema.default : {};
                const persistedObjectData = (persistedValue !== undefined && typeof persistedValue === 'object' && persistedValue !== null)
                                           ? persistedValue : {};
                
                // Filter out null values from persisted data to prevent overriding defaults
                const filteredPersistedData = {};
                Object.entries(persistedObjectData).forEach(([persistedKey, persistedVal]) => {
                    if (persistedVal !== null) {
                        filteredPersistedData[persistedKey] = persistedVal;
                    }
                });
                
                // Recursively process nested properties to get their defaults
                const nestedContextPath = `${stateContextPathForStorage}.${currentRelativePath}`;
                const processedNestedObject = processSchemaSlice(propSchema, nestedContextPath, loggerInstance);
                
                // Merge: schema defaults < processed nested defaults < persisted data (excluding nulls)
                finalValueToSet = { ...schemaDefaultObject, ...processedNestedObject, ...filteredPersistedData };
                
                if (isPersisted && JSON.stringify(finalValueToSet) !== JSON.stringify(persistedValue)) {
                    try {
                        localSettings.set(_getLocalStorageKey(stateContextPathForStorage, currentRelativePath), finalValueToSet);
                    } catch (error) {
                        loggerInstance.error('Failed to persist object value during initialization', { context: stateContextPathForStorage, path: currentRelativePath, error });
                    }
                }
            } else {
                if (persistedValue !== undefined && persistedValue !== null) {
                    finalValueToSet = persistedValue;
                } else if ('default' in propSchema) {
                    finalValueToSet = propSchema.default;
                    if (isPersisted) {
                        try {
                            localSettings.set(_getLocalStorageKey(stateContextPathForStorage, currentRelativePath), finalValueToSet);
                        } catch (error) {
                            loggerInstance.error('Failed to persist non-object default value during initialization', { context: stateContextPathForStorage, path: currentRelativePath, error });
                        }
                    }
                } else {
                    finalValueToSet = undefined;
                }
            }
            if (finalValueToSet !== undefined) {
                currentContextState = _setNestedValueImmutable(currentContextState, currentRelativePath, finalValueToSet);
            }
        });
        return currentContextState;
    };

    logger.debug("Initializing global/shared contexts...");
    if (appStateSchema) {
        logger.debug("Initializing context: app at state path: app");
        state.app = processSchemaSlice(appStateSchema, 'app', logger);
    } else {
        logger.warn("appStateSchema is not defined. 'app' context will not be initialized.");
        state.app = {}; // Ensure 'app' key exists
    }

    if (batchStateSchema) { // This is for the global state.batches
        logger.debug("Initializing context: batches (global) at state path: batches");
        state.batches = processSchemaSlice(batchStateSchema, 'batches', logger);
    } else {
        logger.warn("batchStateSchema is not defined. Global 'batches' context will not be initialized.");
        state.batches = {}; // Ensure 'batches' key exists
    }

    if (redistributionStateSchema) {
        logger.debug("Initializing context: redistribution at state path: redistribution");
        state.redistribution = processSchemaSlice(redistributionStateSchema, 'redistribution', logger);
    } else {
        state.redistribution = {};
    }

    logger.debug("Initializing page-specific contexts from pageSchemas...");
    Object.entries(pageSchemas).forEach(([pageName, pageSchema]) => {
        if (pageSchema && typeof pageSchema === 'object') {
            const stateContextPathForStorage = `pages.${pageName}`;
            logger.debug(`Initializing page context: ${pageName} at state path: ${stateContextPathForStorage}`);
            // state.pages must exist, ensured by initial state = { pages: {} }
            state.pages[pageName] = processSchemaSlice(pageSchema, stateContextPathForStorage, logger);
        } else {
            logger.warn(`Invalid schema for page '${pageName}'. Initialization skipped.`);
            state.pages[pageName] = {}; // Ensure page key exists under pages
        }
    });

    logger.debug("Initial state constructed:", state);
    return state;
};


// --- Reducer ---
const appReducer = (state, action, logger) => {
    const { type, payload } = action;
    const { contextName: stateContextPath, path: relativePath, value } = payload || {};

    const getTargetSlice = (currentState) => {
        if (!stateContextPath) {
            logger.warn(`Reducer: stateContextPath is undefined.`);
            return null;
        }
        if (stateContextPath.startsWith('pages.')) {
            const pageName = stateContextPath.substring(6);
            if (!currentState.pages || !currentState.pages.hasOwnProperty(pageName)) {
                logger.warn(`Reducer: Page context '${pageName}' not found in state.pages.`);
                return null;
            }
            return currentState.pages[pageName];
        } else {
            if (!currentState.hasOwnProperty(stateContextPath)) {
                logger.warn(`Reducer: Context '${stateContextPath}' not found in state.`);
                return null;
            }
            return currentState[stateContextPath];
        }
    };

    const updateStateSlice = (currentState, newSlice) => {
        if (!stateContextPath) {
            logger.warn(`Reducer: Cannot update slice for undefined stateContextPath.`);
            return currentState;
        }
        if (stateContextPath.startsWith('pages.')) {
            const pageName = stateContextPath.substring(6);
            return {
                ...currentState,
                pages: {
                    ...(currentState.pages || {}), 
                    [pageName]: newSlice,
                },
            };
        } else {
            return { ...currentState, [stateContextPath]: newSlice };
        }
    };

    switch (type) {
        case 'SET_STATE_PROPERTY': {
            const currentSlice = getTargetSlice(state);
            if (currentSlice === null || currentSlice === undefined) {
                logger.error(`Invalid context for SET_STATE_PROPERTY: ${stateContextPath}`);
                return state;
            }
            const currentValue = _getNestedValue(currentSlice, relativePath);
            const valueChanged = currentValue !== value;

            if (!valueChanged) return state;
            const newSlice = _setNestedValueImmutable(currentSlice, relativePath, value);
            const newState = updateStateSlice(state, newSlice);

            return newState;
        }
        case 'MERGE_CONTEXT_STATE': { 
            const currentSlice = getTargetSlice(state) || {};
            if (!value || typeof value !== 'object') {
                logger.warn(`MERGE_CONTEXT_STATE called with non-object value for context '${stateContextPath}'. State not updated.`);
                return state;
            }
            let changed = false;
            for (const key in value) {
                if (Object.hasOwnProperty.call(value, key)) {
                    if (currentSlice === null || currentSlice[key] !== value[key]) {
                        changed = true;
                        break;
                    }
                }
            }
            if (!changed && currentSlice !== null) return state; 
            const newSlice = { ...currentSlice, ...value };
            return updateStateSlice(state, newSlice);
        }
        case 'ADD_STATE_ITEM': {
            const currentSlice = getTargetSlice(state);
            if (currentSlice === null || currentSlice === undefined) {
                logger.error(`Invalid context for ADD_STATE_ITEM: ${stateContextPath}`);
                return state;
            }
            let currentArray = _getNestedValue(currentSlice, relativePath);
            if (!Array.isArray(currentArray)) {
                if (currentArray === undefined) {
                    logger.warn(`Target path for ADD_STATE_ITEM ('${stateContextPath}.${relativePath}') is undefined, initializing as array.`);
                    currentArray = []; // Initialize as empty array before adding
                } else {
                    logger.error(`Target path for ADD_STATE_ITEM ('${stateContextPath}.${relativePath}') is not an array. Found: ${typeof currentArray}`);
                    return state;
                }
            }
            const newArray = [...currentArray, value];
            const newSlice = _setNestedValueImmutable(currentSlice, relativePath, newArray);
            return updateStateSlice(state, newSlice);
        }
        default:
            logger.warn(`Unhandled action type: ${type}`);
            return state;
    }
};


export const AppStateProvider = ({ children }) => {
    const logger = useLogger('AppStateProvider');
    const initialStateValue = useMemo(() => _initializeState(logger), [logger]); 
    const reducerWithLogger = useCallback((state, action) => appReducer(state, action, logger), [logger]);
    const [state, dispatch] = useReducer(reducerWithLogger, initialStateValue);

    // Ref that mirrors state but updates synchronously when dispatch is called.
    // This eliminates stale-read races in async continuations (e.g. rapid stow operations).
    const stateRef = useRef(initialStateValue);
    stateRef.current = state; // Keep ref in sync with React's committed state

    const syncDispatch = useCallback((action) => {
        stateRef.current = reducerWithLogger(stateRef.current, action);
        dispatch(action);
    }, [reducerWithLogger]);

    const getState = useCallback((params) => {
        const providerName = 'AppStateProvider getState';
        // logger.debug(`getState called`, providerName, { params }); // Keep logging minimal for frequent calls

        if (params === undefined) return stateRef.current;
        if (typeof params !== 'object' || params === null) {
            const errorMsg = 'Invalid params object provided to getState.';
            logger.error(errorMsg, providerName, { params });
            throw new ProviderError(errorMsg, providerName, { params });
        }

        const { page, context, path: relativePath } = params;
        let schemaToUse;
        let stateContextPath;

        if (page && context) {
            const errorMsg = "`getState` cannot accept both 'page' and 'context' parameters simultaneously.";
            logger.error(errorMsg, providerName, { params });
            throw new ProviderError(errorMsg, providerName, { params });
        }

        if (page) {
            stateContextPath = `pages.${page}`;
            if (pageSchemas.hasOwnProperty(page)) {
                schemaToUse = pageSchemas[page];
            } else {
                logger.error(`Schema not found for page: '${page}' in pageSchemas.`, providerName, { params });
                throw new ProviderError(`Schema not found for page: '${page}'`, providerName, { params });
            }
        } else if (context) {
            stateContextPath = context;
            if (context === 'app' && appStateSchema) {
                schemaToUse = appStateSchema;
            } else if (context === 'batches' && batchStateSchema) {
                schemaToUse = batchStateSchema;
            } else if (context === 'redistribution' && redistributionStateSchema) {
                schemaToUse = redistributionStateSchema;
            } else {
                logger.error(`Schema not found or unhandled context: '${context}'.`, providerName, { params });
                throw new ProviderError(`Schema not found or unhandled context: '${context}'`, providerName, { params });
            }
        } else { // No page or context provided
            const errorMsg = "`getState` requires either a 'page' or a 'context' parameter when params object is provided.";
            logger.error(errorMsg, providerName, { params });
            throw new ProviderError(errorMsg, providerName, { params });
        }

        const currentState = stateRef.current;
        let targetSlice;
        if (stateContextPath.startsWith('pages.')) {
            const pageNameKey = stateContextPath.substring(6);
            if (!currentState.pages || !currentState.pages.hasOwnProperty(pageNameKey)) {
                logger.warn(`Page context '${pageNameKey}' not found in state.pages. Available: ${Object.keys(currentState.pages || {}).join(', ')}`, providerName, { params });
                return undefined;
            }
            targetSlice = currentState.pages[pageNameKey];
        } else {
            if (!currentState.hasOwnProperty(stateContextPath)) {
                logger.warn(`Context '${stateContextPath}' not found in state. Available: ${Object.keys(currentState).filter(k => k !== 'pages').join(', ')}`, providerName, { params });
                return undefined;
            }
            targetSlice = currentState[stateContextPath];
        }

        if (!relativePath) return targetSlice;
        const nestedValue = _getNestedValue(targetSlice, relativePath);

        if (nestedValue === undefined && schemaToUse) { // Check schemaToUse exists
            // logger.debug(`Value at path '${stateContextPath}.${relativePath}' is undefined. Checking schema for default.`, providerName);
            const schemaProperty = _getSchemaProperty(schemaToUse, relativePath);
            if (schemaProperty && schemaProperty.hasOwnProperty('default')) {
                // logger.debug(`Returning default value from schema for path '${schemaLookupKey}.${relativePath}'.`, providerName, { defaultValue: schemaProperty.default });
                return schemaProperty.default;
            } else {
                // logger.debug(`No default value found in schema for path '${schemaLookupKey}.${relativePath}'. Returning undefined.`, providerName);
            }
        }
        return nestedValue;
    }, [logger]);

    const setState = useCallback((params) => {
        const providerName = 'AppStateProvider setState';
        const { path: relativePath, value, page, context } = params;

        let schemaToUse;
        let stateContextPath;
        let schemaLookupKey;
        let actionType;

        if (page && context) {
            const errorMsg = "`setState` cannot accept both 'page' and 'context' parameters simultaneously.";
            logger.error(errorMsg, providerName, { params });
            throw new ProviderError(errorMsg, providerName, { params });
        }

        if (page) {
            stateContextPath = `pages.${page}`;
            schemaLookupKey = page;
            if (pageSchemas.hasOwnProperty(page)) {
                schemaToUse = pageSchemas[page];
            } else {
                logger.error(`Schema not found for page: '${page}' in pageSchemas.`, providerName, { params });
                throw new ProviderError(`Schema not found for page: '${page}'`, providerName, { params });
            }
        } else if (context) {
            stateContextPath = context;
            schemaLookupKey = context;
            if (context === 'app' && appStateSchema) {
                schemaToUse = appStateSchema;
            } else if (context === 'batches' && batchStateSchema) {
                schemaToUse = batchStateSchema;
            } else if (context === 'redistribution' && redistributionStateSchema) {
                schemaToUse = redistributionStateSchema;
            } else {
                logger.error(`Schema not found or unhandled context: '${context}'.`, providerName, { params });
                throw new ProviderError(`Schema not found or unhandled context: '${context}'`, providerName, { params });
            }
        } else if (relativePath && !page && !context) { // Default to 'app' if only path is given
            stateContextPath = 'app';
            schemaLookupKey = 'app';
            schemaToUse = appStateSchema;
        } else {
            const errorMsg = "`setState` requires 'page', 'context', or at least 'path' (for default 'app' context).";
            logger.error(errorMsg, providerName, { params });
            throw new ProviderError(errorMsg, providerName, { params });
        }
        
        if (!schemaToUse) { // Should be caught above, but as a safeguard
            const error = new Error(`Schema could not be loaded for '${schemaLookupKey}'. Cannot perform setState.`);
            logger.error(error.message, providerName, { relativePath, value, stateContextPath });
            throw new ProviderError(error.message, providerName, { relativePath, value, stateContextPath });
        }

        if (relativePath) {
            actionType = 'SET_STATE_PROPERTY';
        } else if (value !== undefined && typeof value === 'object' && value !== null && !Array.isArray(value)) {
            actionType = 'MERGE_CONTEXT_STATE';
        } else {
            const errorMsg = 'setState requires a valid relativePath, or a non-null, non-array object value when relativePath is absent (for context merge).';
            logger.error(errorMsg, providerName, { relativePath, value, stateContextPath });
            throw new ProviderError(errorMsg, providerName, { relativePath, value, stateContextPath });
        }

        const actionForValidation = { type: actionType, path: relativePath, payload: value };

        try {
            StateActionValidator.validateAction(actionForValidation, logger, schemaToUse);
            // logger.debug(`Action ${actionType} validated successfully against schema for ${schemaLookupKey}`, providerName, { relativePath });

            if (actionType === 'SET_STATE_PROPERTY' && relativePath) { // Ensure relativePath for SET_STATE_PROPERTY
                if (_shouldPersistProperty(schemaToUse, relativePath)) {
                    const storageKey = _getLocalStorageKey(stateContextPath, relativePath);
                    const currentSliceForValueCheck = getState({ [page ? 'page' : 'context']: schemaLookupKey }); // Get the slice to check current value
                    const currentValue = currentSliceForValueCheck ? _getNestedValue(currentSliceForValueCheck, relativePath) : undefined;
                    const valueChanged = currentValue !== value;
                    if (valueChanged) {
                        localSettings.set(storageKey, value);
                        // logger.debug('Persisted changed state via setState', { storageKey, value });
                    }
                }
            } else if (actionType === 'MERGE_CONTEXT_STATE') {
                Object.keys(value).forEach(keyInMergedObject => {
                    if (_shouldPersistProperty(schemaToUse, keyInMergedObject)) {
                        const storageKey = _getLocalStorageKey(stateContextPath, keyInMergedObject);
                        const newValueForKey = value[keyInMergedObject];
                        const currentSliceForValueCheck = getState({ [page ? 'page' : 'context']: schemaLookupKey });
                        const oldValueForKey = currentSliceForValueCheck ? _getNestedValue(currentSliceForValueCheck, keyInMergedObject) : undefined;
                        if (newValueForKey !== oldValueForKey) {
                            localSettings.set(storageKey, newValueForKey);
                            // logger.debug('Persisted changed key from MERGE_CONTEXT_STATE', { storageKey, key: keyInMergedObject, newValueForKey });
                        }
                    }
                });
            }

            const dispatchPayload = { contextName: stateContextPath, path: relativePath, value };
            syncDispatch({ type: actionType, payload: dispatchPayload });
            // logger.debug(`Dispatched ${actionType} action successfully`, providerName, { stateContextPath, relativePath: relativePath || '(context level)' });
        } catch (error) {
            const errorMsg = `Error during setState (${actionType}) for context '${stateContextPath}' (schema '${schemaLookupKey}'): ${error.message}`;
            logger.error(errorMsg, providerName, { relativePath, value, originalError: error });
            throw new ProviderError(errorMsg, providerName, { originalError: error, relativePath, value, stateContextPath, schemaLookupKey });
        }
    }, [syncDispatch, logger, getState]);

    const addState = useCallback((params) => {
        const providerName = 'AppStateProvider addState';
        const { path: relativePath, value, page, context } = params;

        let schemaToUse;
        let stateContextPath;
        let schemaLookupKey;

        if (page && context) {
            const errorMsg = "`addState` cannot accept both 'page' and 'context' parameters simultaneously.";
            logger.error(errorMsg, providerName, { params });
            throw new ProviderError(errorMsg, providerName, { params });
        }
        
        if (page) {
            stateContextPath = `pages.${page}`;
            schemaLookupKey = page;
            if (pageSchemas.hasOwnProperty(page)) {
                schemaToUse = pageSchemas[page];
            } else {
                logger.error(`Schema not found for page: '${page}' in pageSchemas.`, providerName, { params });
                throw new ProviderError(`Schema not found for page: '${page}'`, providerName, { params });
            }
        } else if (context) {
            stateContextPath = context;
            schemaLookupKey = context;
            if (context === 'app' && appStateSchema) {
                schemaToUse = appStateSchema;
            } else if (context === 'batches' && batchStateSchema) {
                schemaToUse = batchStateSchema;
            } else if (context === 'redistribution' && redistributionStateSchema) {
                schemaToUse = redistributionStateSchema;
            } else {
                logger.error(`Schema not found or unhandled context: '${context}'.`, providerName, { params });
                throw new ProviderError(`Schema not found or unhandled context: '${context}'`, providerName, { params });
            }
        } else { // addState always requires page or context
             const errorMsg = "`addState` requires either a 'page' or a 'context' parameter.";
             logger.error(errorMsg, providerName, { params });
             throw new ProviderError(errorMsg, providerName, { params });
        }
        
        if (!schemaToUse) {
             const error = new Error(`Schema could not be loaded for '${schemaLookupKey}'. Cannot perform addState.`);
             logger.error(error.message, providerName, { relativePath, value, stateContextPath });
             throw new ProviderError(error.message, providerName, { relativePath, value, stateContextPath });
        }
        if (!relativePath) {
            const errorMsg = '`addState` requires a valid `relativePath`.';
            logger.error(errorMsg, providerName, { stateContextPath, value });
            throw new ProviderError(errorMsg, providerName, { relativePath, value, stateContextPath });
        }

        const targetSchemaProperty = _getSchemaProperty(schemaToUse, relativePath);
        if (!targetSchemaProperty || targetSchemaProperty.type !== 'array') {
            const errorMsg = `Target path for \`addState\` ('${schemaLookupKey}.${relativePath}') is not defined as an array in the schema.`;
            logger.error(errorMsg, providerName, { stateContextPath, relativePath, schema: targetSchemaProperty });
            throw new ProviderError(errorMsg, providerName, { stateContextPath, relativePath, schema: targetSchemaProperty });
        }

        const currentArray = getState({ [page ? 'page' : 'context']: schemaLookupKey, path: relativePath }) || [];
        if (!Array.isArray(currentArray)) { // Should be an array due to || [] or schema validation
            logger.error(`Target path for \`addState\` ('${stateContextPath}.${relativePath}') is not an array despite checks. Found: ${typeof currentArray}`);
            throw new ProviderError(`Target path for \`addState\` ('${stateContextPath}.${relativePath}') is not an array.`, providerName, { currentValue: currentArray });
        }

        const actionForValidation = { type: 'ADD_STATE_ITEM', path: relativePath, payload: value };
        try {
            StateActionValidator.validateAction(actionForValidation, logger, schemaToUse);
            // logger.debug(`Action ADD_STATE_ITEM validated successfully against schema for ${schemaLookupKey}`, providerName, { relativePath });

            if (targetSchemaProperty?.persist === true) {
                const storageKey = _getLocalStorageKey(stateContextPath, relativePath);
                const newArrayToPersist = [...currentArray, value];
                localSettings.set(storageKey, newArrayToPersist);
                // logger.debug('Persisted updated array state via addState', { storageKey, newArrayToPersist });
            }

            const dispatchPayload = { contextName: stateContextPath, path: relativePath, value };
            syncDispatch({ type: 'ADD_STATE_ITEM', payload: dispatchPayload });
            // logger.debug('Dispatched ADD_STATE_ITEM action successfully', providerName, { stateContextPath, relativePath });
        } catch (error) {
            const errorMsg = `Error during addState (ADD_STATE_ITEM) for context '${stateContextPath}' (schema '${schemaLookupKey}'): ${error.message}`;
            logger.error(errorMsg, providerName, { relativePath, value, originalError: error });
            throw new ProviderError(errorMsg, providerName, { originalError: error, relativePath, value, stateContextPath, schemaLookupKey });
        }
    }, [syncDispatch, logger, getState]);


    const contextValue = useMemo(() => ({
        state,
        setState,
        getState,
        addState,
        logger // Expose logger if needed by consumers, though internal logging is primary
    }), [state, setState, getState, addState, logger]);

    // Expose state on window for E2E testing (non-prod or test environments)
    // Expose state on non-prod environments for E2E testing
    const isTestEnv = process.env.REACT_APP_BB_ENV !== 'prod';
    if (isTestEnv) {
        window.__APP_STATE__ = state;
    }

    return (
        <AppStateContext.Provider value={contextValue}>
            {children}
        </AppStateContext.Provider>
    );
};

AppStateProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAppState = () => {
    const logger = useLogger('useAppState'); // Each hook instance gets its own logger if needed
    const context = useContext(AppStateContext);
    if (!context) {
        const errorMsg = 'useAppState must be used within an AppStateProvider';
        logger.error(errorMsg, 'useAppState'); // Log from the hook's logger instance
        throw new ProviderError(errorMsg); // No need to pass logger to ProviderError from here
    }
    return context;
};

export const useAppStateSelector = (selector) => {
    const contextValue = useContext(AppStateContext);
    if (!contextValue) {
        // No logger instance here easily, unless we create one or AppStateContext provides it.
        // For now, direct throw.
        throw new ProviderError('useAppStateSelector must be used within an AppStateProvider (context is undefined)');
    }
    const { state } = contextValue;
    return selector(state);
};
